const { Type } = require('@sinclair/typebox');
const { merge } = require('lodash');
const Blockly = require('blockly');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const {validationReporter} = require('../../../../helpers/validationReporter');
const { config, moduleRegistration } = require('@sstdev/lib_metadata-config');
const logging = require('@sstdev/lib_logging');
const convertToBoolean = require('../../../../helpers/convertToBoolean');

const TOOLTIP =
    'Non displayed field element, to force API to accept a code generated field, or to provide a default/fixed value for new records';

function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    blocklyBlock.setTooltip(TOOLTIP);
    const blockId = blocklyBlock.id;
    const workspace = blocklyBlock.workspace;

    //set inputs to be arranged horizontally (this is the default)
    //Meaning each (dummy)input continues on the top line followed by  the access modifiers
    blocklyBlock.setInputsInline(false);
    // line1 already includes the title
    line1OfInputs.appendField('  Property Name:').appendField(new Blockly.FieldTextInput(''), 'propertyName');

    // Only show data formats if formats are needed (based on dataType)
    // For instance, string data types can have formats like email, long-text, date-time, etc.
    // but an integer is just an integer. So display "None" instead.
    const dataTypeDropDown = new Blockly.FieldDropdown(() => Object.keys(config.dataTypeMapping).map(k => [k, k]));
    line1OfInputs.appendField(' Data Type:').appendField(dataTypeDropDown, 'overrideDataType');

    let nonValidatingFormatDropDown = new Blockly.FieldDropdown(() => getFormats(workspace, blockId));
    nonValidatingFormatDropDown.doClassValidation_ = function (newValue) {
        const dataType = blocklyBlock.getFieldValue('overrideDataType');
        const targetDataType = config.dataTypeMapping[dataType];
        if (!newValue || !dataType || !targetDataType) return newValue;
        if (!targetDataType.formats || targetDataType.formats[newValue]) return newValue;
        const property = blocklyBlock.getFieldValue('propertyName') || blocklyBlock.getFieldValue('title');
        const { page, form } = getParents(blocklyBlock);
        logging.warn(
            `${page}>${form}> Invisible element for ${property} has datatype ${dataType} which cannot have format ${newValue}`
        );
        return null;
    };
    line1OfInputs.appendField('  Format:').appendField(nonValidatingFormatDropDown, 'overrideDataFormat');
    line1OfInputs.appendField('  Default Value:').appendField(new Blockly.FieldTextInput(''), 'defaultValue');

    line1OfInputs.appendField('  Required:').appendField(new Blockly.FieldCheckbox('FALSE'), 'required');
    line1OfInputs.appendField('  Tooltip:').appendField(new Blockly.FieldTextInput(''), 'tooltip');
    line1OfInputs.appendField('  Exclude from Import:').appendField(new Blockly.FieldCheckbox(), 'excludeFromImport');

    blocklyBlock
        .appendStatementInput('nestedInvisibleElements')
        .setCheck(['formElement'])
        .appendField('Nested Invisible Elements:');
}

function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    addCodeProperties([
        'propertyName',
        'defaultValue',
        { key: 'required', handler: convertToBoolean },
        'tooltip',
        { key: 'excludeFromImport', handler: convertToBoolean },
        {
            key: 'sequence',
            // Song and dance to figure out the sequence based on order in the workspace.
            handler: () => {
                const parent = blocklyBlock.getSurroundParent();
                // if the block is not docked, it won't have a parent.
                // Therefore, our sequence # is by definition 0:
                if (!parent) return 0;
                const parentChildren = parent.getDescendants(false);
                return parentChildren.findIndex(value => value.id === blocklyBlock.id) - 1; // subtract 1 because includes parent with getDescendants
            }
        }
    ]);

    const dataType = blocklyBlock.getFieldValue('overrideDataType');
    const format = blocklyBlock.getFieldValue('overrideDataFormat');
    const property = blocklyBlock.getFieldValue('propertyName') || blocklyBlock.getFieldValue('title');
    const targetDataType = config.dataTypeMapping[dataType];
    if (format && dataType && targetDataType && targetDataType.formats) {
        if (!targetDataType.formats[format]) {
            const { page, form } = getParents(blocklyBlock);
            validationReporter(
                `${page}>${form}> Invisible element for ${property} has datatype ${dataType} which cannot have format ${format}`,
                blocklyBlock.id
            );
        }
    }

    addCodeProperties(['overrideDataType', 'overrideDataFormat']);
    return code;
}

// Take the hNode definition and generate a data dictionary (fragment) for the data this block would generate.
/** Builds the TypeBox schema fragment for the InvisibleElement form element. */
function asDataDictionary(hNode, context) {
    const { id, title, propertyName, dataType, overrideDataType, format, overrideDataFormat, required, defaultValue } = hNode;

    const subDataDictionary = {};

    // Process nested invisible elements if present
    let nestedProperties = {};
    if (hNode.children) {
        const [_subDataDictionary = {}, _partialTypeDefinition = {}] = hNode.children.reduce(
            ([sdd, ptd], child) => {
                const [childSubDict = {}, childPartial = {}] = context.extractFromChild(child, context);
                return [
                    merge(sdd, childSubDict),
                    moduleRegistration.mergePartialTypeDefinitions(ptd, childPartial)
                ];
            },
            [{}, {}]
        );
        nestedProperties = _partialTypeDefinition;
    }

    // reuse consistent flags
    const { isRequired, isEditable, isOptional } = moduleRegistration.resolveFieldFlags(required, context);

    // Determine the actual data type and format to use
    const actualDataType = overrideDataType || dataType || 'string';
    const actualFormat = overrideDataFormat || format;

    // Build the base options for all schema types
    const baseOptions = {
        title: title,
        'x-id': id,
        'x-dataType': actualDataType,
        'x-propertyName': propertyName,
        'x-required': isRequired,
        'x-optional': isOptional,
        'x-editable': isEditable
    };

    // Add format if present (standard formats use 'format', custom use 'x-format')
    // Standard JSON Schema 2020-12 formats: email, date-time, date, time, uri, etc.
    if (actualFormat) {
        const standardFormats = [
            'date-time', 'date', 'time', 'duration',
            'email', 'idn-email',
            'hostname', 'idn-hostname',
            'ipv4', 'ipv6',
            'uri', 'uri-reference', 'iri', 'iri-reference', 'uri-template',
            'json-pointer', 'relative-json-pointer',
            'regex'
        ];

        if (standardFormats.includes(actualFormat)) {
            baseOptions.format = actualFormat;
        } else {
            baseOptions['x-format'] = actualFormat;
        }
    }

    // Add default value if present and valid for the data type
    // Empty strings are not valid defaults for non-string types
    if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
        // Convert default value to appropriate type if needed
        const normalizedDataType = actualDataType.toLowerCase();
        let typedDefault = defaultValue;

        if (normalizedDataType === 'bool' || normalizedDataType === 'boolean') {
            // Convert string booleans to actual booleans
            if (typeof defaultValue === 'string') {
                typedDefault = defaultValue.toLowerCase() === 'true';
            }
        } else if (normalizedDataType === 'number' || normalizedDataType === 'float' || normalizedDataType === 'double') {
            typedDefault = parseFloat(defaultValue);
            if (isNaN(typedDefault)) typedDefault = undefined;
        } else if (normalizedDataType === 'integer' || normalizedDataType === 'int') {
            typedDefault = parseInt(defaultValue, 10);
            if (isNaN(typedDefault)) typedDefault = undefined;
        }
        // For arrays and other complex types, don't set a string default
        else if (normalizedDataType === 'array' || normalizedDataType === 'object') {
            typedDefault = undefined;
        }

        if (typedDefault !== undefined) {
            baseOptions.default = typedDefault;
        }
    }

    // Helper to wrap schema as optional if needed
    const maybeOptional = schema => (isOptional ? Type.Optional(schema) : schema);

    // Create the appropriate TypeBox schema based on data type
    let schema;
    const hasNestedElements = Object.keys(nestedProperties).length > 0;

    if (hasNestedElements) {
        // Build the object schema for the nested properties
        const objectSchema = Type.Object(
            nestedProperties,
            {
                ...baseOptions,
                additionalProperties: true
            }
        );

        // Check if this should be an array of objects
        if (actualDataType.toLowerCase() === 'array') {
            schema = Type.Array(objectSchema, baseOptions);
        } else {
            schema = objectSchema;
        }
    } else {
        // Create schema based on data type
        switch (actualDataType.toLowerCase()) {
            case 'array':
                schema = maybeOptional(
                    Type.Array(
                        Type.Object(
                            {},
                            {
                                title: 'Item',
                                'x-dataType': 'object',
                                additionalProperties: true
                            }
                        ),
                        baseOptions
                    )
                );
                break;
            case 'bool':
            case 'boolean':
                schema = maybeOptional(Type.Boolean(baseOptions));
                break;
            case 'number':
            case 'float':
            case 'double':
                schema = maybeOptional(Type.Number(baseOptions));
                break;
            case 'integer':
            case 'int':
                schema = maybeOptional(Type.Integer(baseOptions));
                break;
            case 'string':
            default:
                schema = maybeOptional(Type.String(baseOptions));
                break;
        }
    }

    // If not using maybeOptional above (for objects), wrap here
    if (hasNestedElements && isOptional) {
        schema = Type.Optional(schema);
    }

    const partialTypeDefinition = {
        [propertyName]: schema
    };

    return [subDataDictionary, partialTypeDefinition];
}

const distinct = arrayOStrings => [...new Set(arrayOStrings)];

function getFormats(workspace, blockId) {
    if (blockId) {
        let block = workspace.getBlockById(blockId);
        let dataType = block.getFieldValue('overrideDataType');
        const targetDataType = config.dataTypeMapping[dataType];
        if (!dataType || !targetDataType) {
            let all = distinct(Object.values(config.dataTypeMapping).flatMap(f => Object.keys(f.formats || {}))).map(
                k => [k, k]
            );

            all.unshift(['Any', '']);
            return all;
        }
        if (!Object.keys(targetDataType.formats || {}).length) {
            return [['Any', '']];
        }
        const validFormats = Object.keys(targetDataType.formats);
        const currentFormat = block.getFieldValue('overrideDataFormat');
        if (currentFormat && !validFormats.includes(currentFormat)) {
            const property = block.getFieldValue('propertyName') || block.getFieldValue('title');
            logging.warn(`Invalid format ${currentFormat} for datatype ${dataType} of property ${property}`);
            block.setFieldValue(null, 'overrideDataFormat');
            block.isDirty_ = true;
        }
        let results = validFormats.map(k => [k, k]);

        return results;
    } else {
        return [['None Defined', '']];
    }
}

function getParents(block) {
    let form = '';
    let page = '';
    let parent = block.parentBlock_;
    while (parent) {
        if (parent.type.startsWith('form_')) {
            form = parent.getFieldValue('title');
        } else if (parent.type.startsWith('page_')) {
            page = parent.getFieldValue('title');
            break;
        }
        parent = parent.parentBlock_;
    }
    return { form, page };
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
